/**
 * Every magic string the flow depends on, in one place.
 *
 * The important one is FLOW_ORDER: the router guard, the progress bar and the resume
 * pointer all read it, so the step sequence is changed here and nowhere else.
 */

/**
 * Step sequence, adopted from the redesign prototype.
 *
 * This is the prototype's order (camera -> video -> sign -> review), not the legacy
 * app's (camera -> sign -> video -> review). Camera and video share one getUserMedia
 * acquisition, so keeping them adjacent avoids stopping and restarting the hardware
 * around a signature detour — on mobile that restart is exactly what yields a black
 * preview. Signature needs no camera and is the natural cool-down before review.
 */
export const FLOW_ORDER = ["capture-image", "capture-video", "select-sign", "check-assets"];

/** Human labels for the step chrome and the resume dialog. */
export const STEP_LABELS = {
  "capture-image": "ثبت عکس چهره",
  "capture-video": "ضبط ویدیوی چهره",
  "select-sign": "ثبت امضای الکترونیک",
  "check-assets": "بازبینی و تأیید",
};

/** localStorage keys. These names are load-bearing — the backend hand-back reads `token`. */
export const STORAGE_KEYS = {
  accessToken: "accessToken",
  id: "id",
  token: "token",
  timer: "timer",
  returnUrl: "returnUrl",
  progress: "jud-ekyc-progress",
};

/**
 * How long the face-framing check gets before both camera steps hand their button back.
 *
 * The detector is not perfect and this is a service people are required to use, so the
 * gate always expires. Counted from the first verdict, not from the camera opening: the
 * runtime may still have been downloading, and burning the window on that would hand the
 * button back before the check ever ran.
 */
export const FRAMING_FALLBACK_MS = 15000;

/** The only challenge type the backend supports. */
export const CHALLENGE = "VIDEOREAD";

/** Recording rules: total length, and how long before saving is allowed. */
export const RECORD_SECONDS = 15;
export const RECORD_MIN_SECONDS = 5;

/**
 * How the challenge clip is captured and encoded.
 *
 * MediaRecorder with no options encodes at whatever the browser thinks is generous — on a
 * modern Android phone that is the full sensor resolution at ~2.5 Mbit/s video plus ~128
 * kbit/s stereo audio. Fifteen seconds of that is ~5 MB, and /jud/finalize carries it as
 * base64 inside a JSON body, which adds a further third: ~6.5 MB uploaded over a phone's
 * uplink. That is what has been timing out mid-submit and dropping people out of the funnel.
 *
 * These numbers target ~1.6 MB on the wire (~2.1 MB base64) for the same 15 seconds, a
 * little under a quarter of the old payload:
 *
 *   - 640x480 at 24 fps is well above what the check needs. The face fills the guide oval,
 *     so it lands roughly 300 px across — more than BlazeFace uses (it works off a 128 px
 *     input) and more than enough for a human reviewer to read lip movement and head turn.
 *   - 800 kbit/s is a comfortable rate for a talking head at that size; the visible cost is
 *     in fast motion, and the only fast motion here is the head turn.
 *   - Speech stays intelligible on Opus far below 32 kbit/s, and one channel is all a single
 *     speaker on a phone mic produces anyway.
 *
 * The container is pinned separately, back to the WebM/VP8 the operator panel has always
 * been given — see MIME_PREFERENCE in utils/media.js. VP9 would buy another 30-40% at the
 * same quality and is the obvious next lever, but it is not worth finding out mid-funnel
 * that the panel's player cannot read it.
 */
export const VIDEO_BITS_PER_SECOND = 800000;
export const AUDIO_BITS_PER_SECOND = 32000;
export const VIDEO_CAPTURE_WIDTH = 640;
export const VIDEO_CAPTURE_HEIGHT = 480;
export const VIDEO_CAPTURE_FPS = 24;

/** Saved progress is offered for resume for one hour, then discarded. */
export const PROGRESS_MAX_AGE_MS = 60 * 60 * 1000;

/** Support line, shown as a tel: link on phones and as a copyable number on desktop. */
export const SUPPORT_PHONE = "02157798000";
export const SUPPORT_PHONE_DISPLAY = "۰۲۱ ۵۷۷۹۸۰۰۰";

/**
 * The قوه‌قضائیه hand-back convention: the referring system is told the flow completed
 * by prefixing the original inbound token with `4:`.
 */
export const RETURN_TOKEN_PREFIX = "4:";
